import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Text, SimpleGrid, Loader, Button } from '@mantine/core'
import { toast } from 'react-toastify'
import { Profile } from '../../app/models/profile'
import { useStore } from '../../app/stores/store'
import { AmmoScoreBadge } from '../../app/common/ui'
import agent from '../../app/api/agent'

const TIER_BORDER: Record<string, string> = {
  Newcomer: '#8B949E',
  Enthusiast: '#58A6FF',
  Contributor: '#3FB950',
  Expert: '#E85D04',
  Curator: '#C9A84C',
  Armorer: '#C9A84C'
}

function getInitials (displayName?: string, username?: string) {
  const name = ((displayName || username || '?').trim())
  const parts = name.split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

interface FollowRowProps {
  user: Profile;
}

const FollowRow = observer(function FollowRow ({ user }: FollowRowProps) {
  const { userStore } = useStore()
  const history = useHistory()
  const [following, setFollowing] = useState(user.isFollowing ?? false)
  const [busy, setBusy] = useState(false)

  const isCurrentUser = userStore.user?.username === user.username
  const canFollow = userStore.isLoggedIn && !isCurrentUser
  const borderColor = TIER_BORDER[user.ammoTier ?? 'Newcomer'] ?? '#8B949E'

  const toggle = async () => {
    if (busy) return
    setBusy(true)
    try {
      if (following) {
        await agent.Profiles.unfollow(user.username)
        setFollowing(false)
      } else {
        await agent.Profiles.follow(user.username)
        setFollowing(true)
      }
    } catch {
      toast.error('Failed to update follow status.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 8,
        background: 'var(--gs-surface)',
        border: '1px solid var(--gs-border)',
        '&:hover': { borderColor: 'var(--gs-border-bright)' }
      }}
    >
      {/* Avatar */}
      <Box
        onClick={() => history.push(`/profiles/${user.username}`)}
        sx={{ flexShrink: 0, cursor: 'pointer' }}
      >
        {user.image
          ? (
          <img
            src={user.image}
            alt={user.displayName}
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              objectFit: 'cover',
              border: `2px solid ${borderColor}`
            }}
          />
            )
          : (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #E85D04, #C9A84C)',
              border: `2px solid ${borderColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 16, color: '#fff' }}>
              {getInitials(user.displayName, user.username)}
            </Text>
          </Box>
            )}
      </Box>

      {/* Info */}
      <Box
        sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        onClick={() => history.push(`/profiles/${user.username}`)}
      >
        <Text
          weight={600}
          size="sm"
          sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-text)', lineHeight: 1.3 }}
          lineClamp={1}
        >
          {user.displayName || user.username}
        </Text>
        <Text size="xs" sx={{ color: 'var(--gs-muted)' }}>
          @{user.username}
        </Text>
        {user.ammoTier && (
          <Box mt={3}>
            <AmmoScoreBadge tier={user.ammoTier} score={user.ammoScore ?? 0} size="sm" />
          </Box>
        )}
      </Box>

      {/* Follow button */}
      {canFollow && (
        <Button
          size="xs"
          variant={following ? 'outline' : 'filled'}
          color={following ? 'gray' : 'orange'}
          loading={busy}
          onClick={toggle}
          sx={{ flexShrink: 0, minWidth: 80 }}
        >
          {following ? 'Unfollow' : 'Follow'}
        </Button>
      )}
    </Box>
  )
})

interface Props {
  username: string;
  mode: 'followers' | 'following';
}

export default observer(function FollowList ({ username, mode }: Props) {
  const { profileStore } = useStore()
  const [list, setList] = useState<Profile[] | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    setList(null);
    (mode === 'followers'
      ? profileStore.loadFollowers(username)
      : profileStore.loadFollowing(username)
    )
      .then((data) => setList(data))
      .catch(() => setList([]))
      .finally(() => setLoading(false))
  }, [username, mode, profileStore])

  if (loading || list === null) {
    return (
      <Box py="xl" sx={{ display: 'flex', justifyContent: 'center' }}>
        <Loader size="sm" />
      </Box>
    )
  }

  if (list.length === 0) {
    return (
      <Box py="xl" sx={{ textAlign: 'center' }}>
        <Text color="dimmed" size="sm">
          {mode === 'followers' ? 'No followers yet.' : 'Not following anyone yet.'}
        </Text>
      </Box>
    )
  }

  return (
    <SimpleGrid cols={1} spacing="xs">
      {list.map((u) => (
        <FollowRow key={u.username} user={u} />
      ))}
    </SimpleGrid>
  )
})
